import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useUI } from '../../../redux/hooks';

const SideMenu = () => {
  const { isSideMenuOpen, closeSideMenu } = useUI();
  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isSideMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSideMenuOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape') {
        closeSideMenu();
      }
    };

    if (isSideMenuOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isSideMenuOpen, closeSideMenu]);

  const menuItems = [
    { title: 'Abogados', href: '/abogados' },
    { title: 'Consultas', href: '/consultas' },
    { title: 'Publicaciones', href: '/publicaciones' },
    { title: '¿Quiénes Somos?', href: '/quienes-somos' },
    { title: 'Soporte', href: '/soporte' },
  ];

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const menuVariants = {
    hidden: {
      x: '100%',
      transition: {
        type: 'spring',
        damping: 30,
        stiffness: 300,
      },
    },
    visible: {
      x: 0,
      transition: {
        type: 'spring',
        damping: 30,
        stiffness: 300,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      x: 20,
      transition: {
        duration: 0.1,
      },
    },
    visible: i => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.02,
        duration: 0.15,
        ease: 'easeOut',
      },
    }),
  };

  return (
    <AnimatePresence>
      {isSideMenuOpen && (
        <>
          {/* Overlay */}
          <motion.div
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
            onClick={closeSideMenu}
          />

          {/* Side Menu */}
          <motion.div
            variants={menuVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="fixed top-0 right-0 h-screen w-64 bg-lawyer-bg z-50"
          >
            {/* Header */}
            <div className="flex items-start justify-end p-4 bg-lawyer-bg">
              <button
                onClick={closeSideMenu}
                className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-700 transition-colors"
                aria-label="Cerrar menú"
              >
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto overscroll-y-contain py-4 bg-lawyer-bg">
              <nav className="space-y-1 px-4 bg-lawyer-bg">
                {menuItems.map((item, index) => (
                  <motion.div
                    key={item.title}
                    custom={index}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className="bg-lawyer-bg"
                  >
                    <Link
                      to={item.href}
                      onClick={closeSideMenu}
                      className="group relative block w-full rounded-xl py-4 px-4 transition-all duration-200 hover:bg-gray-700 bg-lawyer-bg"
                    >
                      <div className="flex flex-col">
                        <div className="flex items-center justify-center">
                          <span className="text-xl font-semibold text-white group-hover:text-blue-400 transition-colors uppercase text-center">
                            {item.title}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </nav>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-700 p-6 bg-lawyer-bg">
              <div className="space-y-3 bg-lawyer-bg">
                <Link
                  to="/login"
                  onClick={closeSideMenu}
                  className="flex items-center justify-center w-full px-4 py-2.5 text-base font-medium text-blue-400 bg-gray-700 rounded-xl hover:bg-gray-600 transition-colors"
                >
                  Iniciar Sesión
                </Link>
                <Link
                  to="/signup"
                  onClick={closeSideMenu}
                  className="flex items-center justify-center w-full px-4 py-2.5 text-base font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors"
                >
                  Crear Cuenta
                </Link>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SideMenu;
